import { accessToken$, impersonateEmail$ } from "$root/functional-areas/session/atom";
import { downloadBlob } from "$root/utils";
import { readAtom } from "@mdotm/shadcn/legacy/reactivity";
import contentDisposition from "content-disposition";
import { sphereAIConfig } from "./config";

/**
 * The operator export of Sphere AI chat history — every owner's conversations, as a
 * file, for a ROOT user.
 *
 * The server side is `GET /v1/admin/conversations/export` in sphere-mcp
 * (src/server/chat/history-export.ts), and it is the one Sphere AI read that is not
 * scoped to the signed-in user: the ROOT role is what authorizes it, checked there
 * and not here. The button that calls this lives in the Global settings panel's
 * Exports menu (../../pages/AdvanceSettings/UserSettingsPanel.tsx), behind the same
 * `<AuthorizationGuard requiredRole="ROOT">` the panel's other operator actions use —
 * so the role is asserted twice, in the two places that can each be reached without
 * the other.
 *
 * Deliberately NOT part of `serverChatStore.ts`, which is the wire for the
 * conversations the signed-in user owns, nor of its repository: nothing here is
 * server state a view renders or a write invalidates, it is a download.
 *
 * ## The file
 *
 * NDJSON — one conversation per line, transcript and all — because the server streams
 * it off a cursor rather than buffering a collection it cannot bound. That shape
 * reaches the browser unchanged: the response is taken as a blob and handed to the
 * download, never parsed, so a 200MB export costs this tab nothing to save.
 *
 * The server names the file, and the name carries what was asked for — the user, the
 * window, or the day it was taken. Nothing here reconstructs it: the two ends would
 * drift, and only one of them knows how many conversations the filters actually
 * matched.
 */

/** The identity headers every Sphere AI wire sends (chat transport, MCP, uploads). */
function authHeaders(): Record<string, string> {
	const accessToken = readAtom(accessToken$);
	const impersonateEmail = readAtom(impersonateEmail$);
	return {
		...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
		...(impersonateEmail ? { "X-Impersonate-Email": impersonateEmail } : {}),
	};
}

/** What to call the saved file when the response carries no usable `Content-Disposition`. */
const FALLBACK_FILE_NAME = "sphere-ai-conversations.ndjson";

function fileNameOf(response: Response): string {
	const header = response.headers.get("content-disposition");
	if (!header) {
		return FALLBACK_FILE_NAME;
	}
	try {
		return contentDisposition.parse(header).parameters.filename ?? FALLBACK_FILE_NAME;
	} catch {
		// A header the parser refuses is not a reason to lose the download the user is
		// already waiting on — save it under the default name instead.
		return FALLBACK_FILE_NAME;
	}
}

/**
 * Download the conversation history, optionally narrowed by date, by user, or both.
 *
 * - `since` is a plain `YYYY-MM-DD` — what a date input produces — and the server reads
 *   it as UTC midnight and matches on each conversation's last activity, so "since
 *   Monday" includes a thread opened last year and written to on Tuesday.
 * - `ownerIds` are Sphere user ids. They travel as a repeated `ownerId` parameter,
 *   which is the shape the server reads.
 *
 * Each is omitted when it has nothing to say, and an omitted one means "all of them" —
 * so no argument at all is the whole store. An EMPTY `ownerIds` is treated as no
 * filter rather than sent: the server refuses to read a blank list as "these users, of
 * which there are none", and this end is where "the picker is empty" is known.
 *
 * Throws on any non-ok status, including the `403` a caller without the ROOT role
 * gets; the call site turns that into the failure toast.
 */
export async function downloadConversationHistory(opts?: { since?: string; ownerIds?: string[] }): Promise<void> {
	const url = new URL(`${sphereAIConfig().apiUrl}/admin/conversations/export`);
	if (opts?.since) {
		url.searchParams.set("since", opts.since);
	}
	for (const ownerId of opts?.ownerIds ?? []) {
		url.searchParams.append("ownerId", ownerId);
	}
	const response = await fetch(url.toString(), { headers: authHeaders() });
	if (!response.ok) {
		throw new Error(`Conversation history export failed (HTTP ${response.status})`);
	}
	downloadBlob(await response.blob(), { fileName: fileNameOf(response) });
}
